<template>
    <div class="pa-ui rma-link-page">
        <header class="rma-link-top">
            <img src="@/assets/logo-bej.svg" alt="Brizola e Japur" class="rma-link-top__logo" />
            <span class="rma-link-top__tag">Envio seguro de documentos</span>
        </header>

        <main class="rma-link-main">
            <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

            <!-- Link invalido, vencido, cancelado ou concluido -->
            <section v-else-if="closed" class="pa-card rma-link-state">
                <v-icon size="44" :color="closed.done ? '#16a34a' : '#64748b'">{{ closed.done ? "mdi-check-circle-outline" : "mdi-link-variant-off" }}</v-icon>
                <h1 class="pa-section-title mt-3">{{ closed.title }}</h1>
                <p class="pa-muted mt-1 mb-0">{{ closed.message }}</p>
            </section>

            <template v-else-if="page">
                <section class="rma-link-head">
                    <div class="pa-label">Fiscalização mensal (RMA){{ page.case_name ? ` · ${page.case_name}` : "" }}</div>
                    <h1 class="pa-title">Documentos de {{ page.competence.label }}</h1>
                    <p class="pa-subtitle">
                        Anexe cada arquivo no documento correspondente. Os arquivos vão direto para a Administração Judicial.
                        Ao terminar, toque em <strong>Concluir envio</strong>.
                    </p>
                    <div class="rma-link-facts">
                        <span><v-icon small>mdi-calendar-clock-outline</v-icon> Prazo: {{ page.competence.due_date_label }}</span>
                        <span><v-icon small>mdi-link-variant</v-icon> Link válido até {{ formatDateTime(page.expires_at) }}</span>
                        <span><v-icon small>mdi-file-check-outline</v-icon> {{ delivered }} de {{ page.items.length }} documentos enviados</span>
                    </div>
                </section>

                <template v-for="group in groups">
                    <div v-if="groups.length > 1" :key="`g-${group.name}`" class="rma-link-company">{{ group.name }}</div>
                    <rma-item-card v-for="item in group.items" :key="item.id" :item="item" :link="token" class="rma-link-item"
                        @updated="onUpdated" @busy="(value) => onBusy(item.id, value)" />
                </template>

                <section class="pa-card rma-link-finish">
                    <div>
                        <div class="pa-section-title">Terminou?</div>
                        <div class="pa-muted pa-small">Depois de concluir, este link deixa de funcionar. Se faltar algo, peça um novo link à Administração Judicial.</div>
                    </div>
                    <v-btn color="primary" depressed large :disabled="busyItems.length > 0" :loading="finishing" @click="finish">
                        <v-icon left>mdi-check</v-icon>Concluir envio
                    </v-btn>
                </section>
            </template>
        </main>

        <footer class="rma-link-footer">
            Brizola e Japur Administração Judicial · Este link dá acesso apenas ao envio dos documentos desta competência.
        </footer>
    </div>
</template>

<script>
import axios from "axios";
import RmaItemCard from "@/components/rma/RmaItemCard.vue";
import { errorMessage, formatDateTime, notifyError } from "@/components/rma/rma.util";
import "@/components/plan-adhesion/pa-ui.scss";

// Pagina publica do link de envio do RMA (sem login): o token na URL e a credencial.
export default {
    name: "RmaShareUpload",

    components: { RmaItemCard },

    data: () => ({
        loading: true,
        page: null,
        closed: null,
        busyItems: [],
        finishing: false,
    }),

    computed: {
        token() {
            return this.$route.params.token;
        },
        delivered() {
            return this.page ? this.page.items.filter((item) => !item.missing).length : 0;
        },
        // Uma secao por empresa quando o link vale para o grupo inteiro.
        groups() {
            if (!this.page) return [];
            const groups = [];
            this.page.items.forEach((item) => {
                let group = groups.find((entry) => entry.name === item.company_name);
                if (!group) {
                    group = { name: item.company_name, items: [] };
                    groups.push(group);
                }
                group.items.push(item);
            });
            return groups;
        },
    },

    created() {
        document.title = "Envio de documentos · Brizola e Japur";
        this.load();
    },

    methods: {
        formatDateTime,

        async load() {
            this.loading = true;
            try {
                const result = await axios.get(`/api/rma-links/${this.token}`);
                this.page = result.data.content;
            } catch (error) {
                this.showClosed(error);
            } finally {
                this.loading = false;
            }
        },

        showClosed(error) {
            const status = error && error.response ? error.response.status : 0;
            const message = errorMessage(error, "Não foi possível abrir o link. Tente novamente em instantes.");
            const done = /conclu/i.test(message);
            this.closed = {
                done,
                title: done ? "Envio concluído" : status === 404 || status === 410 ? "Link indisponível" : "Não foi possível abrir",
                message,
            };
        },

        onUpdated(content) {
            if (content && content.items) this.page = content;
        },

        onBusy(itemId, value) {
            this.busyItems = value ? this.busyItems.concat(itemId) : this.busyItems.filter((id) => id !== itemId);
        },

        async finish() {
            const missing = this.page.items.filter((item) => item.missing && item.can_upload).length;
            const confirmed = await this.$dialog.confirm({
                title: "Concluir envio",
                text: missing
                    ? `Ainda ${missing === 1 ? "falta 1 documento" : `faltam ${missing} documentos`}. Concluir mesmo assim? O link deixa de funcionar.`
                    : "Todos os documentos foram enviados. Concluir? O link deixa de funcionar.",
                actions: { false: "Voltar", true: "Concluir" },
            });
            if (!confirmed) return;
            this.finishing = true;
            try {
                await axios.post(`/api/rma-links/${this.token}/finish`);
                this.closed = { done: true, title: "Envio concluído", message: "Obrigado! A Administração Judicial já recebeu os arquivos." };
            } catch (error) {
                if (error && error.response && error.response.status === 410) this.showClosed(error);
                else notifyError(this, error, "Não foi possível concluir. Tente novamente.");
            } finally {
                this.finishing = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.rma-link-page { min-height: 100vh; background: #f8fafc; display: flex; flex-direction: column; }
.rma-link-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 20px; background: #fff; border-bottom: 1px solid #e2e8f0; }
.rma-link-top__logo { height: 34px; }
.rma-link-top__tag { font-size: 12px; color: #64748b; }
.rma-link-main { width: 100%; max-width: 880px; margin: 0 auto; padding: 24px 16px 32px; flex: 1; }
.rma-link-head { margin-bottom: 18px; }
.rma-link-facts { display: flex; flex-wrap: wrap; gap: 8px 18px; margin-top: 12px; font-size: 13px; color: #334155; }
.rma-link-facts .v-icon { color: #64748b; margin-right: 2px; }
.rma-link-company { margin: 20px 0 8px; font-size: 13px; font-weight: 600; color: #27306b; text-transform: uppercase; letter-spacing: 0.04em; }
.rma-link-item { margin-bottom: 12px; }
.rma-link-finish { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 18px 20px; margin-top: 20px; flex-wrap: wrap; }
.rma-link-state { text-align: center; padding: 40px 24px; margin-top: 24px; }
.rma-link-footer { padding: 16px; text-align: center; font-size: 12px; color: #94a3b8; }
@media (max-width: 600px) {
    .rma-link-top__tag { display: none; }
    .rma-link-finish .v-btn { width: 100%; }
}
</style>
